import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Zap, Check, Lock, HelpCircle, ArrowRight } from 'lucide-react';
import { createCreditCheckoutSession, fetchAllCreditProducts } from '../../redux/PaymentSlice/paymentSlice';

const PLAN_META = {
    100: {
        name: 'Starter', tagline: 'Try it out', per: '$0.09',
        features: ['100 credits', 'Recruiter lookups', 'AI Assistant', 'Never expire'],
    },
    300: {
        name: 'Pro', tagline: 'Most popular', per: '$0.08',
        features: ['300 credits', 'Recruiter lookups', 'AI Assistant', 'Hiring signal scans', 'Priority support'],
        popular: true,
    },
    700: {
        name: 'Power', tagline: 'Save 22% per credit', per: '$0.07',
        features: ['700 credits', 'Everything in Pro', 'Bulk recruiter exports', 'Strategy reports', 'Carry over forever'],
        best: true,
    },
};

const getPlanMeta = (credits) => PLAN_META[credits] || { name: 'Plan', tagline: '', per: '', features: [] };

function PlanCard({ plan, credits, priceUsd, isSelected, onClick }) {
    const meta = getPlanMeta(credits);
    const sel = isSelected;
    const ink = sel ? '#fff' : '#0F172A';
    const muted = sel ? 'rgba(255,255,255,.78)' : '#64748B';

    return (
        <div
            onClick={onClick}
            className="relative flex flex-col gap-3.5 cursor-pointer transition-all duration-200 text-left"
            style={{
                padding: '22px 22px 20px',
                borderRadius: 18,
                background: sel
                    ? 'linear-gradient(155deg, #4F62E8 0%, #3F57E0 55%, #2C3DBD 100%)'
                    : '#fff',
                border: sel ? '1px solid rgba(63,87,224,.4)' : '1px solid #E6E8EE',
                boxShadow: sel
                    ? '0 18px 40px rgba(63,87,224,.35), inset 0 1px 0 rgba(255,255,255,.18)'
                    : '0 1px 4px rgba(15,23,42,.06)',
                transform: sel ? 'translateY(-4px)' : 'none',
            }}
        >
            {/* Badge */}
            {meta.best && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest text-white px-2.5 py-1 rounded-full"
                    style={{ background: '#10B981', boxShadow: '0 4px 10px rgba(16,185,129,.25)' }}>
                    Best value
                </span>
            )}
            {meta.popular && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full"
                    style={{ background: '#FBBF24', color: '#7c2d12', boxShadow: '0 4px 10px rgba(251,191,36,.35)' }}>
                    ★ Most popular
                </span>
            )}

            {/* Name + radio */}
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-[.12em]" style={{ color: sel ? 'rgba(255,255,255,.7)' : '#64748B' }}>
                        {meta.name}
                    </div>
                    <div className="text-[11.5px] font-medium mt-1" style={{ color: muted }}>{meta.tagline}</div>
                </div>
                <div className="flex-shrink-0 w-5 h-5 rounded-full grid place-items-center"
                    style={{
                        border: sel ? '2px solid #fff' : '1.5px solid #CBD5E1',
                        background: sel ? 'rgba(255,255,255,.18)' : '#fff',
                    }}>
                    {sel && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
            </div>

            {/* Price */}
            <div>
                <div className="flex items-baseline gap-1.5">
                    <span className="text-[36px] font-extrabold leading-none" style={{ letterSpacing: '-0.03em', color: ink }}>${priceUsd}</span>
                    <span className="text-[13px] font-semibold" style={{ color: muted }}>USD</span>
                </div>
                <div className="flex items-baseline gap-1.5 mt-2">
                    <span className="text-[24px] font-extrabold leading-none" style={{ letterSpacing: '-0.02em', color: ink }}>{credits}</span>
                    <span className="text-[13px] font-semibold" style={{ color: muted }}>credits</span>
                </div>
            </div>

            <div style={{ height: 1, background: sel ? 'rgba(255,255,255,.2)' : '#EFF1F5' }} />

            {/* Per-credit pill */}
            <div className="inline-flex self-start text-[11.5px] font-semibold px-2.5 py-1 rounded-full"
                style={{
                    background: sel ? 'rgba(255,255,255,.15)' : '#F6F7FB',
                    color: sel ? '#fff' : '#334155',
                }}>
                {meta.per} / credit
            </div>

            {/* Features */}
            <ul className="flex flex-col gap-1.5 list-none p-0 m-0">
                {meta.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-[12.5px] font-medium" style={{ color: sel ? 'rgba(255,255,255,.92)' : '#334155' }}>
                        <span className="flex-shrink-0 w-4 h-4 rounded-full grid place-items-center"
                            style={{
                                background: sel ? 'rgba(255,255,255,.22)' : '#ECFDF3',
                                color: sel ? '#fff' : '#067647',
                            }}>
                            <Check size={9} strokeWidth={3} />
                        </span>
                        {f}
                    </li>
                ))}
            </ul>
        </div>
    );
}

const BuyCreditsModal = ({ isOpen, onClose }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { creditProducts, creditProductsLoading, creditProductsError } = useSelector((state) => state.payments);

    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState(null);
    const [productsLoaded, setProductsLoaded] = useState(false);

    const fallbackPlans = [
        { id: 'fallback-starter', unit_amount: 900,  metadata: { credits: 100 }, isFallback: true },
        { id: 'fallback-pro',     unit_amount: 2400, metadata: { credits: 300 }, isFallback: true },
        { id: 'fallback-power',   unit_amount: 4900, metadata: { credits: 700 }, isFallback: true },
    ];

    const backendAvailable = Array.isArray(creditProducts) && creditProducts.length > 0;
    const displayPlans = backendAvailable ? creditProducts : fallbackPlans;
    const checkoutUnavailable = !backendAvailable;

    useEffect(() => {
        if (isOpen && (!productsLoaded || creditProducts.length === 0)) {
            dispatch(fetchAllCreditProducts()).then(() => setProductsLoaded(true));
        }
    }, [isOpen, dispatch, productsLoaded, creditProducts.length]);

    // Default to Pro (300 credits) on open
    useEffect(() => {
        if (!creditProductsLoading && displayPlans.length > 0) {
            const stillExists = displayPlans.some(p => p.id === selectedId);
            if (!selectedId || !stillExists) {
                const pro = displayPlans.find(p => Number(p.metadata?.credits) === 300) || displayPlans[1] || displayPlans[0];
                setSelectedId(pro?.id ?? displayPlans[0].id);
            }
        }
    }, [displayPlans, creditProductsLoading, selectedId]);

    if (!isOpen) return null;

    const selectedPlan = displayPlans.find(p => p.id === selectedId);
    const selectedPriceUsd = selectedPlan ? (Number(selectedPlan.unit_amount) / 100).toFixed(2) : '0.00';

    const handleCheckout = async () => {
        if (!selectedId) return;
        if (String(selectedId).startsWith('fallback-')) {
            setCheckoutError('Checkout is unavailable until live Stripe plans are loaded.');
            return;
        }
        setLoading(true);
        setCheckoutError(null);
        try {
            const response = await dispatch(createCreditCheckoutSession({ priceId: selectedId })).unwrap();
            if (response?.url) { window.location.href = response.url; return; }
            setCheckoutError('Checkout URL was not returned. Please try again.');
        } catch (err) {
            setCheckoutError(err?.message || err || 'Could not connect to payment gateway. Please try again.');
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-6"
            style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(8px)' }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white flex flex-col w-full relative overflow-hidden"
                style={{ maxWidth: 920, borderRadius: 20, boxShadow: '0 30px 80px rgba(15,23,42,.45)', maxHeight: '95vh' }}>

                {/* Header */}
                <div className="flex items-center gap-3.5 px-7 py-6 border-b border-[#EFF1F5] flex-shrink-0">
                    <div className="w-11 h-11 rounded-xl grid place-items-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #EEF1FE 0%, #C7D0F8 100%)', color: '#3F57E0', boxShadow: 'inset 0 1px 0 #fff' }}>
                        <Zap size={20} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                        <div className="text-[18px] font-bold text-[#0F172A] font-dmsans">Select a credit plan</div>
                        <div className="text-[12px] text-[#64748B] font-dmsans mt-0.5">One-time top-up · credits never expire on Pro &amp; Power plans</div>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold text-[#334155] flex-shrink-0"
                        style={{ background: '#F6F7FB', border: '1px solid #E6E8EE' }}>
                        Current balance ·{' '}
                        <span className="font-extrabold" style={{ color: '#3F57E0' }}>{user?.creditsBalance ?? 0}</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-[#334155] hover:bg-[#F4F5F8] transition-colors flex-shrink-0"
                        aria-label="Close"
                    >
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="overflow-y-auto flex-1">
                    {/* Plans */}
                    <div className="px-7 pt-7 pb-2">
                        {creditProductsLoading ? (
                            <div className="grid grid-cols-3 gap-4">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="rounded-[18px] border border-[#E6E8EE] p-6 flex flex-col gap-3 animate-pulse">
                                        <div className="h-3 w-16 bg-[#F4F5F8] rounded" />
                                        <div className="h-9 w-20 bg-[#F4F5F8] rounded" />
                                        <div className="h-6 w-24 bg-[#F4F5F8] rounded" />
                                        <div className="h-px bg-[#F4F5F8]" />
                                        <div className="h-4 w-20 bg-[#F4F5F8] rounded-full" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 gap-4 items-stretch">
                                {displayPlans.map(plan => {
                                    const credits = Number(plan.metadata?.credits || 0);
                                    const priceUsd = (Number(plan.unit_amount || 0) / 100).toFixed(0);
                                    return (
                                        <PlanCard
                                            key={plan.id}
                                            plan={plan}
                                            credits={credits}
                                            priceUsd={priceUsd}
                                            isSelected={selectedId === plan.id}
                                            onClick={() => setSelectedId(plan.id)}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* How credits work */}
                    <div className="px-7 pt-5 pb-2">
                        <div className="flex gap-3.5 items-start p-4 rounded-[14px] text-left"
                            style={{ background: 'linear-gradient(135deg, #EEF1FE 0%, #F8FAFF 80%)', border: '1px solid #C7D0F8' }}>
                            <div className="w-9 h-9 rounded-[10px] flex-shrink-0 grid place-items-center bg-white"
                                style={{ color: '#3F57E0', border: '1px solid #C7D0F8' }}>
                                <HelpCircle size={16} />
                            </div>
                            <div>
                                <div className="text-[13px] font-bold mb-1" style={{ color: '#3F57E0' }}>How credits work</div>
                                <p className="text-[12.5px] text-[#334155] leading-relaxed">
                                    Credits power recruiter lookups and AI replies. Most actions cost 1 credit — heavier batch operations cost more. Hiring signals are free. Unused credits{' '}
                                    <strong className="text-[#0F172A]">roll over forever</strong> on Pro and Power plans.
                                </p>
                                <div className="flex gap-2 mt-2.5 flex-wrap">
                                    {['1 credit · recruiter lookup', '~2 credits · AI Assistant reply', 'Free · hiring signals'].map(c => (
                                        <span key={c} className="px-2.5 py-1 rounded-full text-[10.5px] font-semibold text-[#334155] border border-[#C7D0F8] bg-white">
                                            {c}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error states */}
                    {checkoutUnavailable && (
                        <div className="mx-7 mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-800 text-[12.5px] flex items-center justify-between">
                            <span>Checkout temporarily unavailable while payment products load.</span>
                            <button type="button" onClick={() => dispatch(fetchAllCreditProducts())} className="ml-3 underline font-semibold">
                                Retry
                            </button>
                        </div>
                    )}
                    {creditProductsError && (
                        <div className="mx-7 mt-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-[12.5px]">
                            Could not load live Stripe plans. Showing fallback plans.
                        </div>
                    )}
                    {checkoutError && (
                        <div className="mx-7 mt-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-[12.5px]">
                            {checkoutError}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center gap-3 px-7 py-5 border-t border-[#EFF1F5] flex-shrink-0">
                    {/* Trust signals */}
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-[11.5px] text-[#64748B] whitespace-nowrap">
                            <Lock size={12} />
                            Secure payment · <strong className="text-[#334155]">Stripe</strong>
                        </div>
                        <div className="flex items-center gap-1.5 text-[11.5px] text-[#64748B] whitespace-nowrap">
                            <Check size={12} className="text-[#10B981]" />
                            7-day money-back
                        </div>
                    </div>
                    {/* Total + actions */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                            <div className="text-[11px] font-semibold text-[#64748B]">You'll be charged</div>
                            <div className="text-[20px] font-extrabold text-[#0F172A] leading-tight" style={{ letterSpacing: '-0.02em' }}>
                                ${selectedPriceUsd} <span className="text-[11px] font-semibold text-[#64748B]">USD</span>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2.5 text-[13px] font-semibold text-[#334155] border border-[#E6E8EE] rounded-xl bg-white hover:bg-[#F6F7FB] transition-colors whitespace-nowrap"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleCheckout}
                            disabled={loading || !selectedId || checkoutUnavailable}
                            className="flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white rounded-xl transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            style={{ backgroundColor: '#3F57E0' }}
                        >
                            <Lock size={13} />
                            {loading ? 'Redirecting…' : checkoutUnavailable ? 'Unavailable' : 'Complete purchase'}
                            {!loading && <ArrowRight size={13} />}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BuyCreditsModal;
